if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          overlay: this.querySelector('.country-selector__overlay'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
        };
        // HR mobile bottom-sheet fix (2026-07-29): on narrow viewports the panel
        // (and its dimming scrim) portal to document.body while open. Some
        // ancestor of the footer's country selector carries a non-"none"
        // transform (the scroll-reveal entrance animation on [data-cascade]),
        // which per spec becomes the containing block for position:fixed
        // descendants — so a real, viewport-docked bottom sheet gets trapped at
        // that ancestor's position instead of the actual screen edge ("floats
        // mid-page"). Portaling is the same technique hr-translate.js already
        // uses for its own language panel, for this exact trap. Desktop is
        // unaffected: it keeps Dawn's normal anchored inline dropdown.
        this.mqlMobile = window.matchMedia('(max-width: 749px)');
        this.panelHome = null;
        this.panelNextSibling = null;
        this.overlayHome = null;
        this.portaled = false;
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.boundCloseSelector = this.closeSelector.bind(this);
        this.addEventListener('focusout', this.boundCloseSelector);
        // Bug fix 2026-08-23: unlike every other optional element here (search,
        // closeButton, resetButton), this.elements.button had no null guard,
        // even though it comes from the same this.querySelector() lookup as the
        // rest. hr-analytics recorded exactly one live hit of "Cannot read
        // properties of null (reading 'addEventListener')" from this file on
        // /pages/research (desktop Chrome, 2026-08-22), which kills every line
        // of this constructor after it for that <localization-form> instance
        // whenever its 'button.localization-form__select' is missing or not
        // yet in the DOM. Guarding just this lookup, not the whole file.
        if (this.elements.button) {
          this.elements.button.addEventListener('click', this.openSelector.bind(this));
        }

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
      }

      hidePanel() {
        // 2026-08-23: the constructor guard alone was not enough. focusout and
        // keyup listeners are bound unconditionally, so these methods run even
        // when button is missing and threw the same null error. Guard at use.
        if (!this.elements.button) return;
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.removeAttribute('data-open');
        if (this.elements.overlay) this.elements.overlay.removeAttribute('data-open');
        // Let the sheet slide/fade out (see CSS transition on [data-open])
        // before detaching it back to its original spot in the DOM.
        var self = this;
        var wasPortaled = this.portaled;
        window.setTimeout(
          function () {
            self.elements.panel.setAttribute('hidden', true);
            if (wasPortaled) self.restorePanel();
          },
          wasPortaled ? 260 : 0
        );
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        document.body.classList.remove('overflow-hidden-mobile');
        document.querySelector('.menu-drawer').classList.remove('country-selector-open');
        this.header.preventHide = false;
      }

      // Moves the panel (and its scrim) to document.body so position:fixed
      // resolves against the real viewport instead of a transformed ancestor.
      // No-op on desktop, where the normal anchored dropdown is used.
      portalPanel() {
        if (this.portaled || !this.mqlMobile.matches) return;
        if (this.elements.panel) {
          this.panelHome = this.elements.panel.parentNode;
          this.panelNextSibling = this.elements.panel.nextSibling;
          document.body.appendChild(this.elements.panel);
        }
        if (this.elements.overlay) {
          this.overlayHome = this.elements.overlay.parentNode;
          document.body.appendChild(this.elements.overlay);
        }
        this.portaled = true;
        // Once portaled, the panel is no longer a DOM descendant of `this`, so
        // the bubble-phase `focusout` listener bound to `this` (constructor)
        // will never see focus events originating inside it — the browser only
        // bubbles along the current ancestor chain. A capture-phase listener
        // on `document` always sees every focusout regardless of tree
        // position, so it stands in for the widget's own listener while
        // portaled. closeSelector's `within()` check already treats the panel
        // as part of the widget, so the exact same close logic applies.
        document.addEventListener('focusout', this.boundCloseSelector, true);
      }

      restorePanel() {
        if (!this.portaled) return;
        if (this.elements.panel && this.panelHome) {
          if (this.panelNextSibling) this.panelHome.insertBefore(this.elements.panel, this.panelNextSibling);
          else this.panelHome.appendChild(this.elements.panel);
        }
        if (this.elements.overlay && this.overlayHome) {
          this.overlayHome.appendChild(this.elements.overlay);
        }
        this.portaled = false;
        document.removeEventListener('focusout', this.boundCloseSelector, true);
      }

      onContainerKeyDown(event) {
        // Query from the panel itself (not `this`): on mobile the panel is
        // portaled to document.body while open (see portalPanel), so it is no
        // longer a descendant of this custom element, and this.querySelectorAll
        // would silently return nothing.
        const focusableItems = Array.from(this.elements.panel.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        if (!this.elements.button) return;
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
      }

      openSelector() {
        if (!this.elements.button) return;
        this.elements.button.focus();
        var opening = this.elements.panel.hasAttribute('hidden');
        if (!opening) {
          // Trigger button re-clicked while the panel is already open: close
          // it the same way every other dismissal path does (hidePanel), so
          // the exit transition and cleanup are consistent everywhere.
          this.hidePanel();
          return;
        }
        this.portalPanel();
        this.elements.panel.removeAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', 'true');
        // Flip data-open a frame after unhiding so the CSS transition (slide
        // up + scrim fade-in) actually animates, instead of jumping straight
        // to the open state in the same paint.
        var self = this;
        requestAnimationFrame(function () {
          self.elements.panel.setAttribute('data-open', 'true');
          if (self.elements.overlay) self.elements.overlay.setAttribute('data-open', 'true');
        });
        // HR iOS fix: do NOT lock body scroll for the country selector. On iOS Safari,
        // body{overflow:hidden} breaks position:sticky, so the header teleports to
        // document-top and "the top bar disappears". The HR country list is short, so a
        // page scroll-lock is unnecessary. (Menu drawer keeps its own lock; it covers the
        // header anyway, so the sticky break is invisible there.) On mobile the new
        // dimming scrim uses touch-action:none to block background scroll-through
        // instead, which never touches body overflow / sticky positioning.
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }
        // Keep the header pinned/visible while the selector is open, so it never hides
        // mid-interaction regardless of the data-prevent-hide attribute.
        if (this.header) {
          this.header.preventHide = true;
        }
        document.querySelector('.menu-drawer').classList.add('country-selector-open');
      }

      closeSelector(event) {
        // "Within the widget" normally means "a descendant of `this`", but on
        // mobile the panel (and everything in it — the search input, the
        // country links) has been portaled to document.body, so it's no
        // longer a DOM descendant of `this` at all. Treat containment in
        // either `this` or the (possibly-portaled) panel as "still inside" so
        // focusing the search field or a country row never mis-fires as
        // "focus left the widget" and slams the sheet shut.
        const within = (el) =>
          !!el && (this.contains(el) || (this.elements.panel && this.elements.panel.contains(el)));
        if (
          (event.target && event.target.classList && event.target.classList.contains('country-selector__overlay')) ||
          !within(event.target) ||
          !within(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        // Scoped to the panel (this.elements.panel), not `this`: on mobile the
        // panel is portaled to document.body while open, so it's no longer a
        // descendant of this custom element and this.querySelector(All) would
        // find nothing, silently breaking the search filter.
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.elements.panel.querySelector('.popular-countries');
        const allCountries = this.elements.panel.querySelectorAll('a');
        let visibleCountries = allCountries.length;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent);
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
            visibleCountries--;
          }
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.elements.panel.scrollTop = 0;
        const list = this.elements.panel.querySelector('.country-selector__list');
        if (list) list.scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
